import client from "@/client/publicApiClient.ts";
import { useMutation } from "@tanstack/react-query";
import type { LoginMutationRequest, LoginMutationResponse, Login400 } from "../types/Login";
import type { UseMutationOptions } from "@tanstack/react-query";

 type LoginClient = typeof client<LoginMutationResponse, Login400, LoginMutationRequest>;
type Login = {
    data: LoginMutationResponse;
    error: Login400;
    request: LoginMutationRequest;
    pathParams: never;
    queryParams: never;
    headerParams: never;
    response: LoginMutationResponse;
    client: {
        parameters: Partial<Parameters<LoginClient>[0]>;
        return: Awaited<ReturnType<LoginClient>>;
    };
};
/**
 * @description This endpoint is used to authenticate a user and return a JWT token that can be used to authenticate future requests.
            
This shouldn't be used for backend service authentication, for that please use the API key authentication.
 * @summary User Login
 * @link /api/auth/login
 */
export function useLogin(options: {
    mutation?: UseMutationOptions<Login["response"], Login["error"], Login["request"]>;
    client?: Login["client"]["parameters"];
} = {}) {
    const { mutation: mutationOptions, client: clientOptions = {} } = options ?? {};
    return useMutation({
        mutationFn: async (data) => {
            const res = await client<Login["data"], Login["error"], Login["request"]>({
                method: "post",
                url: `/api/auth/login`,
                data,
                headers: { "Content-Type": "text/json", ...clientOptions.headers },
                ...clientOptions
            });
            return res.data;
        },
        ...mutationOptions
    });
}